// Database setup and health check script for PGC Whitelist Form

import { supabaseService } from '../services/supabaseClient';
import { emailService } from '../services/emailService';

/**
 * Initialize database tables and settings
 */
export async function initializeDatabase() {
  console.log('Initializing database...');
  
  try {
    // Test the database connection first
    const isConnected = await supabaseService.testConnection();
    
    if (!isConnected) {
      console.error('❌ Failed to connect to the database. Please check your Supabase configuration.');
      console.log('\n🔧 To fix this:\n1. Verify your SUPABASE_URL and SUPABASE_ANON_KEY in the .env file\n2. Make sure your Supabase project is set up correctly\n3. Run the SQL schema from supabase-schema.sql in your Supabase SQL Editor\n');
      return false;
    }
    
    console.log('✅ Database connection successful');
    
    // Test email service
    const isEmailServiceWorking = await emailService.testEmailService();
    
    if (isEmailServiceWorking) {
      console.log('✅ Email service is working');
    } else {
      console.log('⚠️  Email service might have issues - will try SMTP fallback');
    }
    
    // Check if whitelist_entries table exists by trying to fetch a count
    try {
      const { data, error } = await supabaseService.client
        .from('whitelist_entries')
        .select('*', { count: 'exact', head: true });
      
      if (error && error.code === '42P01') { // Undefined table
        console.warn('⚠️  Whitelist entries table does not exist. You need to run the database schema.');
        console.log('\n🔧 Run the SQL from supabase-schema.sql in your Supabase SQL Editor to create the tables.');
      } else {
        console.log('✅ Whitelist entries table is accessible');
      }
    } catch (error) {
      console.log('⚠️  Could not verify whitelist entries table existence:', error.message);
    }
    
    // Check if users table exists
    try {
      const { error } = await supabaseService.client
        .from('users')
        .select('*', { count: 'exact', head: true });
      
      if (error && error.code === '42P01') { // Undefined table
        console.warn('⚠️  Users table does not exist. You need to run the database schema.');
        console.log('\n🔧 Run the SQL from supabase-schema.sql in your Supabase SQL Editor to create the tables.');
      } else {
        console.log('✅ Users table is accessible');
      }
    } catch (error) {
      console.log('⚠️  Could not verify users table existence:', error.message);
    }
    
    console.log('\n🎉 Database initialization completed!');
    return true;
    
  } catch (error) {
    console.error('❌ Error during database initialization:', error);
    return false;
  }
}

/**
 * Get basic health check of the application
 */
export async function healthCheck() {
  console.log('Performing health check...\n');
  
  const results = {
    database: false,
    emailService: false,
    schema: false
  };
  
  try {
    // Check database
    results.database = await supabaseService.testConnection();
    console.log(`📊 Database: ${results.database ? '✅ Connected' : '❌ Disconnected'}`);
    
    // Check email service
    results.emailService = await emailService.testEmailService();
    console.log(`📧 Email Service: ${results.emailService ? '✅ Working' : '❌ Issues'}`);
    
    // Check if we can access the main table
    try {
      const { error } = await supabaseService.client
        .from('whitelist_entries')
        .select('id')
        .limit(1);
      
      results.schema = !error;
      console.log(`🗃️  Schema: ${results.schema ? '✅ OK' : '❌ Missing tables'}`);
    } catch (error) {
      console.log(`🗃️  Schema: ❌ Error checking - ${error.message}`);
    }
    
    const allGood = Object.values(results).every(status => status);
    console.log(`\nOverall Status: ${allGood ? '✅ All systems operational' : '⚠️ Some issues detected'}`);
    
    if (!allGood) {
      console.log('\nSuggestions:');
      if (!results.database) console.log('  - Check your Supabase configuration in .env');
      if (!results.schema) console.log('  - Run the SQL schema from supabase-schema.sql');
      if (!results.emailService) console.log('  - Check your email configuration or set up SMTP');
    }
    
    return results;
    
  } catch (error) {
    console.error('Health check error:', error);
    return results;
  }
}

// If this script is run directly
if (typeof window === 'undefined' && import.meta.url === `file://${process.argv[1]}`) {
  initializeDatabase().then(success => {
    if (success) {
      console.log('\n🚀 The PGC Whitelist Form is properly configured!');
    } else {
      console.log('\n❌ Please address the issues above before running the application.');
    }
  }).catch(console.error);
}

export default { initializeDatabase, healthCheck };